import { TestStatus, WittyIcon } from '@agilelab/plugin-wb-platform';
import { useLazyQuery } from '@apollo/client';
import { ErrorPanel } from '@mainstage/core-components';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  CircularProgress,
  IconButton,
  List,
  TableCell,
  TableRow,
  useTheme,
} from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  GOVERNANCE_ENTITIES,
  GovernanceEntitiesQueryType,
} from '../../../graphql/governanceEntities';
import { useEditorPageContext } from '../context/useEditorPageContext';
import { Test } from '../types';
import { useTestResults } from './hooks/useTestResult';
import { TaskListItem } from './TaskListItem/TaskListItem';
import { TestResults } from './TestResults';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';

interface Props {
  test?: Test | null;
  loadingText?: string;
  startDate?: string;
  testLoading?: boolean;
}

export const TestCurrentRow: FC<Props> = ({
  test,
  testLoading,
  loadingText,
}) => {
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const { setIsAssistantDrawerOpen, assistantState } = useEditorPageContext();

  const [init, setInit] = useState(Boolean(test));

  /*
   * Workaround for screen reader announcements on the TestStatus component
   * (which uses role="status"):
   * When TestStatus mounts with RUNNING already set, screen readers do not
   * detect a change and therefore never announce it. To force the RUNNING announcement the first time
   * we initially render TestStatus with NOT_STARTED (`init = false`), then
   * immediately flip to RUNNING (`init = true`) via this effect, so the
   * transition is picked up by the role="status" region.
   */
  useEffect(() => {
    setInit(true);
  }, []);

  // to be shown while loading
  const placeholderTest: Test = {
    id: 'placeholder',
    startDate: new Date().toISOString(),
    status: !init ? DagStatus.NOT_STARTED : DagStatus.RUNNING,
    tasks: [],
  };

  const [fetchPolicies, { loading: taskLoading, error, data }] =
    useLazyQuery<GovernanceEntitiesQueryType>(GOVERNANCE_ENTITIES, {
      variables: {
        ids: test
          ? test.tasks
              .filter(t => !!t.governanceEntityId)
              .map(p => p.governanceEntityId)
          : [],
      },
    });

  useEffect(() => {
    if (!data && test) {
      fetchPolicies();
    }
  }, [fetchPolicies, data, test]);

  const { policiesWithWarnings } = useTestResults(test ?? placeholderTest);

  const shownTest = test ?? placeholderTest;

  const hasAssistant = useMemo(
    () =>
      configApi.getOptionalBoolean('assistant.enabled') &&
      configApi.getOptionalString('assistant.url') &&
      Boolean(localStorage.getItem('assistant')),
    [configApi],
  );

  return (
    <>
      <TableRow selected>
        <TableCell width="5%">
          <TestStatus
            status={shownTest.status}
            okWithWarnings={!!policiesWithWarnings}
            variant="reduced"
          />
        </TableCell>
        <TableCell>
          {format(parseISO(shownTest.startDate), 'yyyy/MM/dd HH:mm:ss')}
        </TableCell>
        <TableCell>
          {!testLoading && test ? <TestResults test={test} /> : loadingText}
        </TableCell>

        {hasAssistant && !testLoading && (
          <TableCell>
            {assistantState.loading ? (
              <CircularProgress size={25} />
            ) : (
              <IconButton
                size="small"
                color="secondary"
                aria-label="toggle witty assistant"
                onClick={() => {
                  setIsAssistantDrawerOpen(true);
                }}
              >
                <WittyIcon />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>

      {!testLoading && test && (
        <TableRow>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            }}
            colSpan={5}
          >
            {taskLoading && (
              <div>
                <CircularProgress />
              </div>
            )}
            {error && <ErrorPanel error={error} />}
            <List aria-label="detail list">
              {!taskLoading &&
                !error &&
                test.tasks.map((t, i) => (
                  <TaskListItem
                    key={`${t.id}_${i}`}
                    task={t}
                    governanceEntities={data?.cgp_governance_entity || []}
                  />
                ))}
            </List>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
